import { useCallback, useMemo } from "react";

import { isEqual, omit } from "lodash-es";
import { useLocalStorage } from "usehooks-ts";

import { getOrderedProperties } from "src/entities/entityView/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const STORAGE_KEY = "entityColumnOrder";

/**
 * Reconciles a stored personal column order with the current schema so the
 * override survives schema edits: properties removed from the schema are
 * dropped, properties added after the order was saved are inserted next to
 * the neighbor they follow in the global order.
 */
export const reconcileColumnOrder = (
  globalOrder: string[],
  storedOrder: string[],
): string[] => {
  const globalKeys = new Set(globalOrder);
  const storedKeys = new Set(storedOrder);
  const keptKeys = storedOrder.filter((key) => globalKeys.has(key));
  const missingKeys = globalOrder.filter((key) => !storedKeys.has(key));

  return missingKeys.reduce((orderedKeys, missingKey) => {
    const precedingKey = globalOrder
      .slice(0, globalOrder.indexOf(missingKey))
      .reverse()
      .find((candidate) => orderedKeys.includes(candidate));
    const insertAt = precedingKey ? orderedKeys.indexOf(precedingKey) + 1 : 0;
    return [
      ...orderedKeys.slice(0, insertAt),
      missingKey,
      ...orderedKeys.slice(insertAt),
    ];
  }, keptKeys);
};

type UseEntityColumnOrderReturn = {
  orderedPropertyKeys: string[];
  hasCustomOrder: boolean;
  setOrder: (keys: string[]) => void;
  resetOrder: () => void;
};

/**
 * Personal, per-browser column order for the entities table. Overlays a
 * localStorage override on top of the global `_order` defined in the entity
 * schema; falls back to the global order when no override is stored.
 */
export const useEntityColumnOrder = (
  entitySchema: EntitySchemaResource,
): UseEntityColumnOrderReturn => {
  const { workspace } = useWorkspaceContext();
  const [storedOrders, setStoredOrders] = useLocalStorage<
    Record<string, string[]>
  >(STORAGE_KEY, {});

  const identifier = `${workspace.id}|${entitySchema._id}`;
  const storedOrder = storedOrders[identifier];

  const { orderedPropertyKeys, hasCustomOrder } = useMemo(() => {
    const globalOrder = getOrderedProperties(entitySchema).filter(
      (propertyKey) => propertyKey !== "id",
    );

    if (!storedOrder) {
      return { orderedPropertyKeys: globalOrder, hasCustomOrder: false };
    }

    const reconciledOrder = reconcileColumnOrder(globalOrder, storedOrder);
    return {
      orderedPropertyKeys: reconciledOrder,
      hasCustomOrder: !isEqual(reconciledOrder, globalOrder),
    };
  }, [entitySchema, storedOrder]);

  const setOrder = useCallback(
    (keys: string[]) => {
      setStoredOrders((prev) => ({ ...prev, [identifier]: keys }));
    },
    [setStoredOrders, identifier],
  );

  const resetOrder = useCallback(() => {
    setStoredOrders((prev) => omit(prev, identifier));
  }, [setStoredOrders, identifier]);

  return { orderedPropertyKeys, hasCustomOrder, setOrder, resetOrder };
};
